import React, {useDeferredValue, useEffect, useRef, useState} from 'react';
import styled from 'styled-components';
import {useDispatch, useSelector} from 'react-redux';
import {addModuleData} from '../../../redux/project';
import {DraggableBox} from '../../UI/DraggableBox';

export const PhotoVisualModule = ({moduleName, number, id, className, onUpdateFields, onRemoveModule}) => {
  const dispatch = useDispatch();
  const data = useSelector(state => state.project.moduleList).find(module => module.id === id)?.data || {};

  const textBoxDefault = {
    position: 'none',
    background: '#eee',
    color: '#000',
    fontSize: 5,
    width: 30,
    height: 30
  };

  const [backgroundColor, setBackgroundColor] = useState(data.backgroundColor || '#fff');
  const [fontFamily, setFontFamily] = useState(data.fontFamily || 'Poppins-Regular');
  const [sliderColor, setSliderColor] = useState(data.sliderColor || '#000');
  const [topPhotoList, setTopPhotoList] = useState(data.topPhotoList || [...Array(2)].map(i => null));
  const [topNameList, setTopNameList] = useState(data.topNameList || [...Array(2)].map((i, n) => `Item #${n + 1}`));
  const [bottomPhotoList, setBottomPhotoList] = useState(data.bottomPhotoList || [...Array(2)].map(i => null));
  const [bottomNameList, setBottomNameList] = useState(data.bottomNameList || [...Array(2)].map((i, n) => `Item #${n + 1}`));
  const [topTextBox, setTopTextBox] = useState(data.topTextBox || textBoxDefault);
  const [bottomTextBox, setBottomTextBox] = useState(data.bottomTextBox || textBoxDefault);
  const [middleTextBox, setMiddleTextBox] = useState(data.middleTextBox || textBoxDefault);

  const [topText, setTopText] = useState(data.topText || 'Changing placeholder text here');
  const [topTextBoxPosition, setTopTextBoxPosition] = useState(data.topTextBoxPosition || [25, 25]);
  const [topTextBoxSize, setTopTextBoxSize] = useState(data.topTextBoxSize || [50, 50]);
  const [topTextBoxLinePosition, setTopTextBoxLinePosition] = useState([0, 0]);
  const topTextBoxContainer = React.useRef(null);

  const [bottomText, setBottomText] = useState(data.bottomText || 'Changing placeholder text here');
  const [bottomTextBoxPosition, setBottomTextBoxPosition] = useState(data.bottomTextBoxPosition || [25, 25]);
  const [bottomTextBoxSize, setBottomTextBoxSize] = useState(data.bottomTextBoxSize || [50, 50]);
  const [bottomTextBoxLinePosition, setBottomTextBoxLinePosition] = useState([0, 0]);
  const bottomTextBoxContainer = React.useRef(null);

  const [middleText, setMiddleText] = useState(data.middleText || 'Changing placeholder text here');
  const [middleTextBoxPosition, setMiddleTextBoxPosition] = useState(data.middleTextBoxPosition || [25, 25]);
  const [middleTextBoxSize, setMiddleTextBoxSize] = useState(data.middleTextBoxSize || [50, 50]);
  const [middleTextBoxLinePosition, setMiddleTextBoxLinePosition] = useState([0, 0]);
  const middleTextBoxContainer = React.useRef(null);

  const container = React.useRef(null);
  const topTextRef = useRef(topText);
  const bottomTextRef = useRef(bottomText);
  const middleTextRef = useRef(middleText);

  const [moduleWidthCoef, setModuleWidthCoef] = useState(0);
  const defferedCoef = useDeferredValue(moduleWidthCoef);

  function removeModule() {
    onRemoveModule(id);
  }

  function updateFields() {
    onUpdateFields({
      moduleName,
      number,
      id,
      backgroundColor,
      setBackgroundColor,
      fontFamily,
      setFontFamily,
      sliderColor,
      setSliderColor,
      topPhotoList,
      setTopPhotoList,
      topNameList,
      setTopNameList,
      bottomPhotoList,
      setBottomPhotoList,
      bottomNameList,
      setBottomNameList,
      topTextBox,
      setTopTextBox,
      bottomTextBox,
      setBottomTextBox,
      middleTextBox,
      setMiddleTextBox,
      removeModule
    });
  }

  useEffect(updateFields, [backgroundColor, fontFamily, sliderColor, topPhotoList, topNameList, bottomPhotoList, bottomNameList, topTextBox, bottomTextBox, middleTextBox]);

  useEffect(() => {
    dispatch(addModuleData({
      id: id,
      data: {
        backgroundColor,
        fontFamily,
        sliderColor,
        topPhotoList,
        topNameList,
        bottomPhotoList,
        bottomNameList,
        topTextBox,
        bottomTextBox,
        middleTextBox,
        topText,
        topTextBoxPosition,
        topTextBoxSize,
        bottomText,
        bottomTextBoxPosition,
        bottomTextBoxSize,
        middleText,
        middleTextBoxPosition,
        middleTextBoxSize,
      }
    }));
  }, [
    backgroundColor,
    fontFamily,
    sliderColor,
    topPhotoList,
    topNameList,
    bottomPhotoList,
    bottomNameList,
    topTextBox,
    bottomTextBox,
    middleTextBox,
    topText,
    topTextBoxPosition,
    topTextBoxSize,
    bottomText,
    bottomTextBoxPosition,
    bottomTextBoxSize,
    middleText,
    middleTextBoxPosition,
    middleTextBoxSize,
  ]);

  function onChangeSize(e) {
    let value = container.current.offsetWidth / window.innerWidth;
    setModuleWidthCoef(value);
  }

  useEffect(() => {
    onChangeSize();
    window.addEventListener('resize', onChangeSize);
    return function () {
      window.removeEventListener('resize', onChangeSize);
    }
  }, []);

  return (
    <StyledModule fontFamily={fontFamily} ref={container} className={className} onClick={updateFields}>
      <Box
        height={`${50 - (middleTextBox.position === 'inside' ? middleTextBox.height / 2 : 0)}%`}
        direction={topTextBox.position === 'right' ? 'row' : 'row-reverse'}
      >
        <BoxPhoto
          width={`${100 - (topTextBox.position !== 'none' ? topTextBox.width : 0)}`}
        >
          {
            topPhotoList.map((item, i) => <PhotoItem key={`${id}_top_${i}`} width={100 / topPhotoList.length} background={item && item.image}></PhotoItem>)
          }
        </BoxPhoto>
        {topTextBox.position !== 'none' && (
          <BoxText
            position={topTextBox.position}
            background={topTextBox.background}
            width={topTextBox.width}
            ref={topTextBoxContainer}
          >
            <DraggableBox
              position={topTextBoxPosition}
              setPosition={setTopTextBoxPosition}
              sizes={topTextBoxSize}
              setSizes={setTopTextBoxSize}
              linePosition={topTextBoxLinePosition}
              setLinePosition={setTopTextBoxLinePosition}
              parentRef={topTextBoxContainer}
            >
              <StyledText
                fontSize={topTextBox.fontSize * defferedCoef}
                color={topTextBox.color}
                onInput={(e) => setTopText(e.target.innerHTML)}
                contentEditable
                dangerouslySetInnerHTML={{__html: topTextRef.current}}
              ></StyledText>
            </DraggableBox>
          </BoxText>)
        }
      </Box>

      <MiddleText
        position={middleTextBox.position}
        background={middleTextBox.background}
        width={middleTextBox.position === 'cover' ? middleTextBox.width : '100'}
        height={middleTextBox.height}
        ref={middleTextBoxContainer}
      >
        <DraggableBox
          position={middleTextBoxPosition}
          setPosition={setMiddleTextBoxPosition}
          sizes={middleTextBoxSize}
          setSizes={setMiddleTextBoxSize}
          linePosition={middleTextBoxLinePosition}
          setLinePosition={setMiddleTextBoxLinePosition}
          parentRef={middleTextBoxContainer}
        >
          <StyledText
            fontSize={middleTextBox.fontSize * defferedCoef}
            color={middleTextBox.color}
            onInput={(e) => setMiddleText(e.target.innerHTML)}
            contentEditable
            dangerouslySetInnerHTML={{__html: middleTextRef.current}}
          ></StyledText>
        </DraggableBox>
      </MiddleText>
      <Box
        height={`${50 - (middleTextBox.position === 'inside' ? middleTextBox.height / 2 : 0)}%`}
        direction={bottomTextBox.position === 'right' ? 'row' : 'row-reverse'}
      >
        <BoxPhoto
          width={`${100 - (bottomTextBox.position !== 'none' ? bottomTextBox.width : 0)}`}
        >
          {
            bottomPhotoList.map((item, i) => <PhotoItem key={`${id}_bottom_${i}`} width={100 / bottomPhotoList.length} background={item && item.image}></PhotoItem>)
          }
        </BoxPhoto>
        {bottomTextBox.position !== 'none' && (
          <BoxText
            position={bottomTextBox.position}
            background={bottomTextBox.background}
            width={bottomTextBox.width}
            ref={bottomTextBoxContainer}
          >
            <DraggableBox
              position={bottomTextBoxPosition}
              setPosition={setBottomTextBoxPosition}
              sizes={bottomTextBoxSize}
              setSizes={setBottomTextBoxSize}
              linePosition={bottomTextBoxLinePosition}
              setLinePosition={setBottomTextBoxLinePosition}
              parentRef={bottomTextBoxContainer}
            >
              <StyledText
                fontSize={bottomTextBox.fontSize * defferedCoef}
                color={bottomTextBox.color}
                onInput={(e) => setBottomText(e.target.innerHTML)}
                contentEditable
                dangerouslySetInnerHTML={{__html: bottomTextRef.current}}
              ></StyledText>
            </DraggableBox>
          </BoxText>)
        }
      </Box>
      <StyledSlider sliderColor={sliderColor.replace('#', '')}/>
    </StyledModule>
  );
};

const StyledModule = styled.div`
  position: absolute;
  top: 5%;
  left: 5%;
  bottom: 5%;
  right: 5%;
  background-repeat: no-repeat;
  background-size: 100%;
  background-position: center;
  box-shadow: 0 0 2px 1px rgba(0 0 0 / 30%);
  font-family: ${props => props.fontFamily};
  &.active {
    box-shadow: 0 0 1px 2px #53A451;
  }
`;

const StyledSlider = styled.div`
  position: absolute;
  top: 10px;
  right: 10px;
  background-image: url("data:image/svg+xml,%3C%3Fxml version='1.0' encoding='utf-8' %3F%3E%3Csvg version='1.1' width='100px' fill='%23${props => props.sliderColor}' height='100px' xmlns='http://www.w3.org/2000/svg' xmlns:xlink='http://www.w3.org/1999/xlink' x='0px' y='0px' viewBox='0 0 240 212' style='enable-background:new 0 0 224 224;' xml:space='preserve'%3E%3Cstyle type='text/css'%3E .st0%7Bfill:%23FFFFFF;%7D .st1%7Bfill:none;stroke:%23fff;stroke-width:3;stroke-linecap:round;stroke-linejoin:round;stroke-miterlimit:10;%7D%0A%3C/style%3E%3Cg id='Layer_1'%3E%3Ccircle class='st0' cx='119.4' cy='106.6' r='77.9' /%3E%3Ccircle cx='119.4' cy='106.6' r='71.4' /%3E%3Cg%3E%3Cpath d='M5335.1,2462.1v-2c0-2.8-5-4-9.7-4s-9.7,1.3-9.7,4v2c0,1.8,0.7,3.6,2,4.9l5,4.9c0.3,0.3,0.4,0.6,0.4,1v6.4 c0,0.4,0.2,0.7,0.6,0.8l2.9,0.9c0.5,0.1,1-0.2,1-0.8v-7.2c0-0.4,0.2-0.7,0.4-1l5.1-5C5334.4,2465.7,5335.1,2463.9,5335.1,2462.1z M5325.4,2462c-4.8,0-7.4-1.3-7.5-1.8l0,0c0.1-0.5,2.7-1.8,7.5-1.8s7.3,1.3,7.5,1.8C5332.7,2460.7,5330.2,2462,5325.4,2462z' /%3E%3Cpath d='M5330.4,2472.3c-0.6,0-1,0.4-1,1s0.4,1,1,1h4.3c0.6,0,1-0.4,1-1s-0.4-1-1-1H5330.4z' /%3E%3Cpath d='M5334.7,2475.7h-4.3c-0.6,0-1,0.4-1,1s0.4,1,1,1h4.3c0.6,0,1-0.4,1-1C5335.7,2476.1,5335.3,2475.7,5334.7,2475.7z' /%3E%3Cpath d='M5334.7,2479h-4.3c-0.6,0-1,0.4-1,1s0.4,1,1,1h4.3c0.6,0,1-0.4,1-1C5335.7,2479.5,5335.3,2479,5334.7,2479z' /%3E%3C/g%3E%3Cg%3E%3C/g%3E%3Cg%3E%3Cpath class='st1' d='M113.8,116V95.4c0-3.1,2.5-5.7,5.7-5.7h0c3.1,0,5.7,2.5,5.7,5.7v28.4' /%3E%3Cpath class='st1' d='M125.1,123.8v-4.6c0-3.1,2.5-5.7,5.7-5.7h0c3.1,0,5.7,2.5,5.7,5.7v7.8' /%3E%3Cpath class='st1' d='M136.4,127.3v-4.6c0-3.1,2.5-5.7,5.7-5.7h0c3.1,0,5.7,2.5,5.7,5.7v7.8' /%3E%3Cpath class='st1' d='M147.8,130.9v-4.6c0-3.1,2.5-5.7,5.7-5.7h0c3.1,0,5.7,2.5,5.7,5.7v21.8' /%3E%3Cpath class='st1' d='M107.1,149L97,138.9c-2.4-2.4-2.4-6.2,0-8.5l0,0c2.4-2.4,6.2-2.4,8.5,0l8.2,8.2l0-22.6' /%3E%3Cg%3E%3Cg%3E%3Cline class='st1' x1='94.1' y1='89.7' x2='69.3' y2='89.7' /%3E%3Cpolyline class='st1' points='78,100.2 67.5,89.7 78,79.3 ' /%3E%3C/g%3E%3Cg%3E%3Cline class='st1' x1='144.7' y1='89.7' x2='169.5' y2='89.7' /%3E%3Cpolyline class='st1' points='160.9,79.3 171.4,89.7 160.9,100.2 ' /%3E%3C/g%3E%3C/g%3E%3Cpath class='st1' d='M130.7,101.4c3-2.9,4.9-7.1,4.9-11.6c0-8.9-7.2-16.2-16.2-16.2c-8.9,0-16.2,7.2-16.2,16.2 c0,4.6,1.9,8.7,4.9,11.6' /%3E%3C/g%3E%3C/g%3E%3C/svg%3E%0A");
  background-repeat: no-repeat;
  background-position: center;
  background-size: 140%;
  width: 50px;
  height: 50px;
  @media (max-width: 1200px) {
    width: 40px;
    height: 40px;
  }
  @media (max-width: 900px) {
    width: 30px;
    height: 30px;
  }
  @media (max-width: 600px) {
    width: 20px;
    height: 20px;
  }
`;

const Box = styled.div`
  height: ${props => props.height};
  display: flex;
  flex-direction: ${props => props.direction};
`;

const BoxPhoto = styled.div`
  width: ${props => props.width + '%'};
  height: 100%;
  display: flex;
  flex-direction: row;
`;

const PhotoItem = styled.div`
  width: ${props => props.width + '%'};
  background: ${props => props.background ? `url(${props.background})` : '#fff'};
  background-position: center;
  background-size: cover;
`;

const BoxText = styled.div`
  overflow: hidden;
  display: ${props => props.position === 'none' ? 'none' : 'block'};
  width: ${props => props.width + '%'};
  height: 100%;
  text-align: center;
  background: ${props => props.background};
  position: relative;
`;

const StyledText = styled.div`
  font-size: ${props => 0.4 * props.fontSize + 'vw'} !important;
  color: ${props => props.color};
  height: 100%;
  line-height: 1.2;
`;

const MiddleText = styled.div`
  overflow: hidden;
  z-index: 1;
  display: ${props => props.position === 'none' ? 'none' : 'block'};
  ${props => props.position === 'cover' ? `
    position: absolute;
    top: 0;
    bottom: 0;
    left: 0;
    right: 0;
    margin: auto;
  ` : `
    position: relative;
  `}
  width: ${props => props.width + '%'};
  height: ${props => props.height + '%'};
  text-align: center;
  background: ${props => props.background};
`;
